/**
 * Mensajes de error HTTP amigables para el usuario final.
 * Nunca se deben exponer detalles técnicos del backend.
 *
 * Principios aplicados (Steve Krug — "Don't Make Me Think"):
 *  - Título específico que describe el problema, no la palabra genérica "Error".
 *  - Lenguaje humano, no jerga técnica ("recurso", "solicitud", "código HTTP").
 *  - Siempre indicar qué puede hacer el usuario para resolver o continuar.
 *  - En errores no recuperables, sugerir explícitamente "contacta al administrador".
 */

/** Título corto y específico para cada status. Va en el `<h3>` del modal. */
export const HTTP_ERROR_TITLES: Record<number, string> = {
  0:   'Sin conexión',
  400: 'Datos inválidos',
  401: 'Sesión expirada',
  403: 'Sin permisos',
  404: 'No encontrado',
  408: 'Tiempo agotado',
  409: 'Conflicto al guardar',
  422: 'Revisa la información',
  429: 'Demasiados intentos',
  500: 'Problema en el servidor',
  502: 'Servicio no disponible',
  503: 'Servicio no disponible',
  504: 'El servidor no responde',
};

/** Título por defecto cuando el status no está mapeado. */
export const DEFAULT_ERROR_TITLE = 'Ocurrió un problema';

export const HTTP_ERROR_MESSAGES: Record<number, string> = {
  0:   'No pudimos conectar con el servidor. Verifica tu conexión a internet y vuelve a intentarlo.',
  400: 'La información enviada no es válida. Revisa los datos del formulario e intenta de nuevo.',
  401: 'Tu sesión ha caducado. Vuelve a iniciar sesión para continuar.',
  403: 'No tienes permisos para realizar esta acción. Si crees que es un error, contacta al administrador.',
  404: 'No encontramos esta información. Es posible que haya sido eliminada o que el enlace esté desactualizado. Si el problema persiste, contacta al administrador.',
  408: 'La respuesta tardó demasiado. Verifica tu conexión y vuelve a intentarlo.',
  409: 'Otro usuario modificó este registro mientras lo editabas. Recarga la página y vuelve a intentar.',
  422: 'Revisa los datos del formulario: algunos campos no cumplen con lo requerido.',
  429: 'Has hecho demasiadas solicitudes en poco tiempo. Espera unos segundos antes de continuar.',
  500: 'Hubo un problema en el servidor. Vuelve a intentarlo en unos minutos. Si persiste, contacta al administrador.',
  502: 'El servicio no está disponible en este momento. Intenta de nuevo en unos minutos. Si persiste, contacta al administrador.',
  503: 'El servicio está temporalmente fuera de línea. Intenta de nuevo más tarde. Si persiste, contacta al administrador.',
  504: 'El servidor tardó demasiado en responder. Intenta de nuevo en unos minutos. Si persiste, contacta al administrador.',
};

export const CONSTRAINT_VIOLATION_MESSAGE =
  'No se puede eliminar este registro porque tiene información relacionada. Elimina primero los datos asociados e inténtalo de nuevo.';

export const DEFAULT_ERROR_MESSAGE =
  'Algo no salió como esperábamos. Intenta de nuevo en unos minutos. Si persiste, contacta al administrador.';

/** Patrones técnicos que indican constraint violation de BD */
export const CONSTRAINT_VIOLATION_PATTERNS: string[] = [
  'constraintviolationexception',
  'could not execute statement',
  'violates foreign key constraint',
  'constraint [fk'
];

/** Patrones técnicos a sanitizar de mensajes backend */
const TECHNICAL_PATTERNS: RegExp[] = [
  /com\.\w+(\.\w+)+/g,                          // Java package names
  /org\.\w+(\.\w+)+/g,                          // Java org packages
  /java\.\w+(\.\w+)+/g,                         // Java core packages
  /javax?\.\w+(\.\w+)+/g,                       // javax packages
  /at\s+[\w.$]+\([\w.]+:\d+\)/g,               // Stack trace lines
  /\b\w+Exception\b/g,                          // Exception class names
  /\b\w+Error:\s/g,                             // Error class prefixes
  /SQL\s*\[.*?\]/gi,                            // SQL fragments
  /could not execute statement/gi,              // Hibernate messages
  /constraint\s*\[[\w.]+\]/gi,                  // Constraint names
  /ERROR:\s+/gi,                                // PostgreSQL error prefix
];

/**
 * Elimina patrones técnicos de un mensaje de error del backend.
 * Si el resultado queda vacío o muy corto, retorna null.
 */
export function sanitizeBackendMessage(raw: string): string | null {
  if (!raw) return null;

  let sanitized = raw;
  for (const pattern of TECHNICAL_PATTERNS) {
    sanitized = sanitized.replace(pattern, '');
  }

  sanitized = sanitized
    .replace(/\s{2,}/g, ' ')
    .replace(/^[\s:;,.-]+/, '')
    .replace(/[\s:;,.-]+$/, '')
    .trim();

  return sanitized.length >= 10 ? sanitized : null;
}
